import type {
  ObjectField,
  ObjectTypeData,
  ObjectFilterCondition,
  ObjectFilterGroup,
  FilterItem,
  ObjectFilterOperator,
  OperatorsByFieldType,
  SelectOption
} from "./types";
import type { ObjectReportColumn } from "../../../dasboards/src/components/createReport/ObjectReports/transformers/schemas";
import {
  SUPPORTED_FIELDS_IN_REPORTING,
  OPERATORS_MAP,
  ALL_OPERATORS,
  SMART_COLUMN_FIELDS,
  OBJECT_FIELD_TYPES
} from "./constants";
import type { OperatorDetails } from "./constants";
import type { IntlShape } from "react-intl";
import { getIndexedFields } from "../../../dasboards/src/components/createReport/ObjectReports/utils/fieldCapabilities";

/**
 * Check if a field type is a date field (date or datetime)
 */
export function isDateField(fieldType: string | null | undefined): boolean {
  return !!(
    fieldType &&
    [OBJECT_FIELD_TYPES.date, OBJECT_FIELD_TYPES.dateTime].includes(
      fieldType as any
    )
  );
}

/**
 * Simple transformation function
 */
function operatorToSelectOption(op: OperatorDetails): SelectOption {
  return {
    label: typeof op.label === "string" ? op.label : String(op.label),
    value: op.value
  };
}

/**
 * Find field in availableFields or smart columns
 */
function findField(
  fieldTag: string,
  availableFields: ObjectField[]
): ObjectField | null {
  // Check availableFields first
  let field = availableFields.find(f => f.tag === fieldTag);

  // If not found, check smart columns
  if (!field) {
    const smartColumn = SMART_COLUMN_FIELDS.find(f => f.tag === fieldTag);
    if (smartColumn) {
      field = {
        tag: smartColumn.tag,
        label: smartColumn.label,
        type: smartColumn.type
      };
    }
  }

  return field || null;
}

/**
 * Get available operators for a specific field
 * Supports configurable default operators
 */
export function getOperatorsForField(
  fieldTag: string | undefined,
  availableFields: ObjectField[],
  operatorsByFieldType: OperatorsByFieldType
): SelectOption[] {
  // Get default operators from config - no fallback, must be explicitly provided
  const defaultOperators = operatorsByFieldType.defaultOperators;
  const defaultOperatorOptions = defaultOperators.map(operatorToSelectOption);

  // No field selected - use configured default operators
  if (!fieldTag) {
    return defaultOperatorOptions;
  }

  // Find the field
  const field = findField(fieldTag, availableFields);

  // Field not found - use configured default operators as fallback
  if (!field) {
    return defaultOperatorOptions;
  }

  // Get operators for this specific field type - they come as complete objects
  const operatorDetails =
    operatorsByFieldType.fieldTypes[
      field.type as keyof typeof operatorsByFieldType.fieldTypes
    ] || [];

  // Direct mapping - no lookups needed
  return operatorDetails.map(operatorToSelectOption);
}

// =============================================================================
// FIELD EXTRACTION UTILITIES
// =============================================================================

function extractFieldsFromSchema(
  objectTypeData: ObjectTypeData
): ObjectField[] {
  const fieldTagsInSchema = Object.keys(objectTypeData.schema.properties);
  const fieldsInTranslatedUiSchema =
    objectTypeData.translatedUiSchema.fields ?? {};

  const fields = fieldTagsInSchema.map(tag => {
    const schemaProperty = objectTypeData.schema.properties[tag];
    return {
      tag,
      label: fieldsInTranslatedUiSchema[tag]?.title || tag,
      type: schemaProperty?.type,
      category: "field" as const
    };
  });

  return fields;
}

export type ReportingFieldsOptions = {
  /** Only include fields that are indexed (can be used in advanced filters) */
  includeOnlyIndexed?: boolean;
  /** Fields that the user has access to - REQUIRED for security */
  accessibleFields: string[];
};

export function getReportingFieldsFromObjectTypeData(
  objectTypeData: ObjectTypeData,
  options: ReportingFieldsOptions
): ObjectField[] {
  if (!objectTypeData?.schema || !options.accessibleFields?.length) {
    return [];
  }

  const allFields = extractFieldsFromSchema(objectTypeData);

  // Filter by supported field types
  let fields = allFields.filter(
    field =>
      field.type &&
      (SUPPORTED_FIELDS_IN_REPORTING as readonly string[]).includes(field.type)
  );

  // Apply access control filtering
  const accessibleFieldsSet = new Set(options.accessibleFields);
  fields = fields.filter(field => accessibleFieldsSet.has(field.tag));

  // Apply indexed field filtering if specified
  if (options.includeOnlyIndexed) {
    const indexedFieldsSet = new Set(getIndexedFields(objectTypeData.schema));
    fields = fields.filter(field => indexedFieldsSet.has(field.tag));
  }

  return fields;
}

export function getOS3SearchFieldsFromObjectTypeData(
  objectTypeData: ObjectTypeData
): ObjectField[] {
  const allFields = extractFieldsFromSchema(objectTypeData);

  let fieldsToReturn = allFields.filter(
    field =>
      field.type &&
      (SUPPORTED_FIELDS_IN_REPORTING as readonly string[]).includes(field.type)
  );

  const sections: Record<string, string[]> = {};

  objectTypeData.translatedUiSchema.detail?.sections?.forEach(section => {
    if (!sections[section.title.toLowerCase()]) {
      sections[section.title.toLowerCase()] = [];
    }
    sections[section.title.toLowerCase()].push(
      ...(section?.fields?.map?.(field => field.tag) || [])
    );
  });

  const sectionsLength = Object.keys(sections).length;
  if (sections?.overview) {
    fieldsToReturn = fieldsToReturn.filter(field => {
      return sections.overview.includes(field.tag);
    });
  } else if (sectionsLength === 0) {
    fieldsToReturn = fieldsToReturn.filter(field => {
      return Object.values(sections).flat().includes(field.tag);
    });
  } else if (sectionsLength > 0) {
    fieldsToReturn = [];
  }

  return fieldsToReturn;
}

// =============================================================================
// TYPE GUARD UTILITIES (for mixed array handling)
// =============================================================================

export function isFilterCondition(
  item: FilterItem
): item is ObjectFilterCondition {
  // Must have field_tag and op, and must NOT have type (which indicates a group)
  return "field_tag" in item && "op" in item && !("type" in item);
}

function isFilterGroup(item: FilterItem): item is ObjectFilterGroup {
  return (
    "type" in item &&
    "conditions" in item &&
    Array.isArray((item as any).conditions)
  );
}

// =============================================================================
// VALIDATION UTILITIES
// =============================================================================

/**
 * Helper function to check if an operator requires a value
 */
function operatorRequiresValue(operator: ObjectFilterOperator): boolean {
  const operatorDetails = ALL_OPERATORS.find(op => op.value === operator);
  // If disableInput is true, the operator doesn't require a value (like isEmpty, isSet)
  return !operatorDetails?.disableInput;
}

/**
 * Helper function to check if a value is considered empty/invalid
 */
function isValueEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === "string") {
    return value.trim() === "";
  }

  if (Array.isArray(value)) {
    return (
      value.length === 0 ||
      value.every(item =>
        typeof item === "string" ? item.trim() === "" : false
      )
    );
  }

  return false;
}

export function sanitizeFilterForAPI(
  filter: ObjectFilterGroup | undefined | null
): ObjectFilterGroup | undefined {
  if (!filter || !isFilterGroup(filter)) {
    return undefined;
  }

  // Remove empty conditions first
  const cleanedFilter = removeEmptyConditions(filter);

  // If no conditions remain after cleaning, return undefined
  if (cleanedFilter.conditions.length === 0) {
    return undefined;
  }

  return cleanedFilter;
}

// =============================================================================
// FILTER CLEANING UTILITIES (remove empty/invalid items)
// =============================================================================

function removeEmptyConditions(group: ObjectFilterGroup): ObjectFilterGroup {
  const cleanedConditions = group.conditions
    .filter(item => {
      if (isFilterCondition(item)) {
        // Remove conditions that are completely empty
        const hasFieldAndOp =
          item.field_tag &&
          item.field_tag.trim() !== "" &&
          item.op &&
          item.op.trim() !== "";

        if (!hasFieldAndOp) {
          return false;
        }

        // Check if the operator requires a value and validate accordingly
        if (operatorRequiresValue(item.op)) {
          // For operators that require values, ensure the value is not empty
          return !isValueEmpty(item.value);
        }

        // For operators that don't require values (like isEmpty, isSet),
        // the condition is valid as long as field_tag and op are present
        return true;
      } else if (isFilterGroup(item)) {
        // Recursively clean nested groups
        const cleanedNestedGroup = removeEmptyConditions(item);
        return cleanedNestedGroup.conditions.length > 0;
      }
      return false;
    })
    .map(item => {
      if (isFilterGroup(item)) {
        return removeEmptyConditions(item);
      }
      return item;
    });

  return {
    ...group,
    conditions: cleanedConditions
  };
}

// =============================================================================
// CONDITION/GROUP MANAGEMENT UTILITIES
// =============================================================================

export function createEmptyCondition(objectTag: string): ObjectFilterCondition {
  return {
    object_tag: objectTag,
    field_tag: "",
    op: getDefaultOperator(),
    value: ""
  };
}

export function createEmptyGroup(): ObjectFilterGroup {
  return {
    type: "all",
    conditions: []
  };
}

export function createDefaultFilter(objectTag: string): ObjectFilterGroup {
  return {
    type: "all",
    conditions: [createEmptyCondition(objectTag)]
  };
}

// =============================================================================
// MIXED ARRAY MANIPULATION UTILITIES
// =============================================================================

export function addConditionToGroup(
  group: ObjectFilterGroup,
  condition: ObjectFilterCondition,
  index?: number
): ObjectFilterGroup {
  const newConditions = [...group.conditions];

  if (index !== undefined) {
    newConditions.splice(index, 0, condition);
  } else {
    newConditions.push(condition);
  }

  return {
    ...group,
    conditions: newConditions
  };
}

export function addGroupToGroup(
  parentGroup: ObjectFilterGroup,
  childGroup: ObjectFilterGroup,
  index?: number
): ObjectFilterGroup {
  const newConditions = [...parentGroup.conditions];

  if (index !== undefined) {
    newConditions.splice(index, 0, childGroup);
  } else {
    newConditions.push(childGroup);
  }

  return {
    ...parentGroup,
    conditions: newConditions
  };
}

export function removeItemFromGroup(
  group: ObjectFilterGroup,
  index: number
): ObjectFilterGroup {
  const newConditions = [...group.conditions];
  newConditions.splice(index, 1);

  return {
    ...group,
    conditions: newConditions
  };
}

export function updateItemInGroup(
  group: ObjectFilterGroup,
  index: number,
  newItem: FilterItem
): ObjectFilterGroup {
  const newConditions = [...group.conditions];
  newConditions[index] = newItem;

  return {
    ...group,
    conditions: newConditions
  };
}

// =============================================================================
// HELPER UTILITIES
// =============================================================================

export function createFieldMapping(
  fields: ObjectField[]
): Record<string, ObjectField> {
  return fields.reduce(
    (mapping, field) => {
      mapping[field.tag] = field;
      return mapping;
    },
    {} as Record<string, ObjectField>
  );
}

export function isConditionComplete(condition: ObjectFilterCondition): boolean {
  if (!isFilterCondition(condition)) return false;

  const hasBasicFields = !!(condition.field_tag && condition.op);
  const isValueRequired = !["is_set", "is_empty"].includes(condition.op);
  const hasValueWhenRequired =
    !isValueRequired ||
    (condition.value !== undefined && condition.value !== "");

  return hasBasicFields && hasValueWhenRequired;
}

// =============================================================================
// ADDITIONAL UTILITY FUNCTIONS (for backward compatibility)
// =============================================================================

/**
 * Get the default operator for a field type (always "equal")
 */
export function getDefaultOperator(): ObjectFilterOperator {
  return OPERATORS_MAP.equal; // This corresponds to "equal" in OPERATORS_MAP
}

// =============================================================================
// ARIA LABEL UTILITIES
// =============================================================================

// Recursive function to generate the aria-label for each nested group
export const buildGroupAriaLabel = (
  groupPathIndexes: number[],
  intl: IntlShape
) => {
  return groupPathIndexes
    .map((groupIndex, index) =>
      intl.formatMessage(
        {
          id: "report.groupNumber",
          defaultMessage: "Group {number}"
        },
        { number: groupIndex + 1 }
      )
    )
    .join(" → ");
};

// =============================================================================
// FIELD EXTRACTION UTILITIES (for report access validation)
// =============================================================================

const extractFieldTagsFromAdvancedFilters = (
  advancedFilters: ObjectFilterGroup | undefined
): string[] => {
  if (!advancedFilters?.conditions) {
    return [];
  }

  const fieldTags = new Set<string>();

  const processConditions = (conditions: FilterItem[]): void => {
    conditions.forEach(item => {
      if (isFilterCondition(item)) {
        // Use existing type guard for consistency
        if (item.field_tag) {
          fieldTags.add(item.field_tag);
        }
      } else if (isFilterGroup(item)) {
        // Recursively process nested groups
        processConditions(item.conditions);
      }
    });
  };

  processConditions(advancedFilters.conditions);
  return Array.from(fieldTags);
};

const extractFieldTagsFromColumns = (
  columns: ObjectReportColumn[] = []
): string[] => {
  const fieldTags = new Set<string>();

  columns.forEach(column => {
    if (column.field_tag) {
      fieldTags.add(column.field_tag);
    }
  });

  return Array.from(fieldTags);
};

export const extractAllReportFieldTags = (
  columns: ObjectReportColumn[] = [],
  advancedFilters: ObjectFilterGroup | undefined
): string[] => {
  const columnFields = extractFieldTagsFromColumns(columns);
  const filterFields = extractFieldTagsFromAdvancedFilters(advancedFilters);

  // Combine and deduplicate
  const allFields = new Set([...columnFields, ...filterFields]);
  return Array.from(allFields);
};
